/**
 * MieRaccolte — Iter122jh
 * Il condòmino loggato vede qui i sondaggi delibera e le richieste dati che lo Studio
 * gli ha inviato (traccia dell'istanza nell'account, non più solo tramite email/token).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Vote, ClipboardList, CheckCircle2, ExternalLink, Clock, AlertTriangle } from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function MieRaccolte() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/mie-raccolte");
        if (alive) setList(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error("Impossibile caricare i sondaggi");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const openRaccolta = (r) => nav(`/raccolta/${r.token}`);
  const fmt = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="mie-raccolte-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-lg bg-indigo-100">
          <Vote className="w-6 h-6 text-indigo-700" />
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">I miei sondaggi</h1>
          <p className="text-sm text-slate-500">Sondaggi di delibera e richieste dati inviate dallo Studio</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500" data-testid="mie-raccolte-loading">Caricamento…</div>
      ) : list.length === 0 ? (
        <Card className="p-8 text-center border-2 border-dashed" data-testid="mie-raccolte-empty">
          <ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <div className="text-slate-600 font-semibold">Nessun sondaggio al momento</div>
          <p className="text-sm text-slate-500 mt-1">Quando lo Studio ti invierà un sondaggio di delibera o una richiesta dati, lo troverai qui.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {list.map((r) => {
            const isSondaggio = r.tipo === "sondaggio_delibera";
            const stato = r.risposto ? "risposto" : (r.scaduta ? "scaduto" : "aperto");
            return (
              <Card key={r.id} className={`p-4 border-2 ${
                stato === "risposto" ? "border-emerald-200 bg-emerald-50/40" :
                stato === "scaduto" ? "border-slate-200 bg-slate-50" :
                "border-indigo-200 bg-indigo-50/40"
              }`} data-testid={`mia-raccolta-${r.raccolta_id}`}>
                <div className="flex items-start gap-3 min-w-0">
                  <div className="shrink-0 mt-0.5">
                    {isSondaggio ? <Vote className="w-5 h-5 text-indigo-700" /> : <ClipboardList className="w-5 h-5 text-slate-700" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-slate-900 min-w-0 break-words">{r.titolo}</h3>
                      {stato === "risposto" && (
                        <Badge className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" /> Risposto</Badge>
                      )}
                      {stato === "aperto" && (
                        <Badge className="bg-indigo-600 hover:bg-indigo-700"><Clock className="w-3 h-3 mr-1" /> Aperto</Badge>
                      )}
                      {stato === "scaduto" && (
                        <Badge className="bg-slate-500 hover:bg-slate-600"><AlertTriangle className="w-3 h-3 mr-1" /> Scaduto</Badge>
                      )}
                    </div>
                    {r.condominio_nome && (
                      <div className="text-xs text-slate-600 mt-0.5">{r.condominio_nome}</div>
                    )}
                    {r.sondaggio_meta && (
                      <div className="text-xs text-slate-700 mt-1 space-y-0.5">
                        {r.sondaggio_meta.fornitore_nome && <div><b>Fornitore:</b> {r.sondaggio_meta.fornitore_nome}</div>}
                        {r.sondaggio_meta.importo && <div><b>Importo:</b> {r.sondaggio_meta.importo}</div>}
                      </div>
                    )}
                    {r.data_scadenza && (
                      <div className="text-xs text-slate-600 mt-1">
                        <b>Scadenza voto:</b> {fmt(r.data_scadenza)}
                      </div>
                    )}
                    {r.risposto_at && (
                      <div className="text-xs text-emerald-700 mt-1">
                        <b>Hai risposto il:</b> {fmt(r.risposto_at)}
                      </div>
                    )}
                    <div className="mt-3">
                      <Button
                        data-testid={`mia-raccolta-apri-${r.raccolta_id}`}
                        onClick={() => openRaccolta(r)}
                        className={stato === "aperto" ? "bg-indigo-700 hover:bg-indigo-800" : "bg-slate-600 hover:bg-slate-700"}
                        size="sm"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        {stato === "risposto" ? "Vedi la mia risposta" : (stato === "scaduto" ? "Vedi (chiuso)" : "Rispondi ora")}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
